import { AxiosResponse } from "axios";
import { APICore } from "./apiCore";
import { IConfiguracoesCurso } from "types/entities/universidade/ConfiguracoesCurso";

const api = new APICore();

// ============ CURSOS (ALUNO) ============

/**
 * Lista todos os cursos cadastrados com progresso do usuário
 */
const listarCursos = (queryString?: string) => {
  return api.get(`/curso${queryString ? queryString : ""}`);
};

/**
 * Busca um curso específico por ID com todos os detalhes
 */
const buscarCursoPorId = (id: string | number) => {
  return api.get(`/curso/${id}`);
};

// ============ ADMIN - CURSOS ============

/**
 * Lista todos os cursos (admin - sem progresso de usuários)
 */
const listarCursosAdmin = (queryString?: string) => {
  return api.get(`/admin/curso${queryString ? queryString : ""}`);
};

/**
 * Busca um curso específico por ID (admin)
 */
const buscarCursoPorIdAdmin = (id: string | number) => {
  return api.get(`/admin/curso/${id}`);
};

/**
 * Cria um novo curso
 * Suporta upload direto de thumbnail via FormData ou URL/chave diretamente
 */
const criarCurso = (
  params:
    | {
        titulo: string;
        descricao: string;
        thumbnail_url?: string;
        thumbnail_key?: string;
      }
    | FormData,
) => {
  // Se for FormData, axios define automaticamente o Content-Type com boundary
  if (params instanceof FormData) {
    return api.create("/admin/curso", params, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  // Caso contrário, usar JSON
  return api.create("/admin/curso", params);
};

/**
 * Atualiza um curso existente
 * Suporta upload direto de thumbnail via FormData ou URL/chave diretamente
 */
const atualizarCurso = (
  id: string | number,
  params:
    | {
        titulo?: string;
        descricao?: string;
        thumbnail_url?: string;
        thumbnail_key?: string;
        ativo?: boolean;
      }
    | FormData,
) => {
  // Se for FormData, axios define automaticamente o Content-Type com boundary
  if (params instanceof FormData) {
    return api.update(`/admin/curso/${id}`, params, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  // Caso contrário, usar JSON
  return api.update(`/admin/curso/${id}`, params);
};

/**
 * Exclui um curso (soft delete)
 */
const deletarCurso = (id: string | number) => {
  return api.delete(`/admin/curso/${id}`);
};

// ============ PROGRESSO ============

/**
 * Lista todos os progressos do usuário autenticado
 */
const listarProgressos = (queryString?: string) => {
  return api.get(`/progresso${queryString ? queryString : ""}`);
};

/**
 * Busca o progresso do usuário em um curso específico
 */
const buscarProgressoCurso = (cursoId: string | number) => {
  return api.get(`/progresso/${cursoId}`);
};

/**
 * Marca um vídeo como assistido e atualiza o progresso
 */
const marcarVideoAssistido = (
  cursoId: string | number,
  videoId: string | number,
) => {
  return api.create(`/progresso/curso/${cursoId}/video/${videoId}`, {});
};

/**
 * Gera o certificado para um curso concluído
 */
const gerarCertificadoCurso = (cursoId: string | number) => {
  return api.create(`/progresso/curso/${cursoId}/certificado/gerar`, {});
};

// ============ QUESTIONÁRIOS ============

/**
 * Busca um questionário por ID com questões e alternativas
 */
const buscarQuestionarioPorId = (id: string | number) => {
  return api.get(`/questionario/${id}`);
};

/**
 * Responde um questionário
 */
const responderQuestionario = (
  id: string | number,
  params: {
    respostas: Array<{
      questao_id: number;
      alternativa_id: number;
    }>;
  },
) => {
  return api.create(`/questionario/${id}/resposta`, params);
};

/**
 * Verifica se o usuário já respondeu o questionário
 */
const verificarRespostaQuestionario = (id: string | number) => {
  return api.get(`/questionario/${id}/resposta`);
};

/**
 * Cria uma nova aula em um curso
 */
const criarAula = (
  cursoId: string | number,
  params: {
    titulo: string;
    descricao?: string;
    ordem: number;
    ultima_aula?: boolean;
  },
) => {
  return api.create(`/admin/curso/${cursoId}/aula`, params);
};

/**
 * Atualiza uma aula existente
 */
const atualizarAula = (
  cursoId: string | number,
  aulaId: string | number,
  params: {
    titulo?: string;
    descricao?: string;
    ordem?: number;
    ultima_aula?: boolean;
  },
) => {
  return api.update(`/admin/curso/${cursoId}/aula/${aulaId}`, params);
};

/**
 * Exclui uma aula
 */
const deletarAula = (cursoId: string | number, aulaId: string | number) => {
  return api.delete(`/admin/curso/${cursoId}/aula/${aulaId}`);
};

/**
 * Marca uma aula como última aula do curso
 */
const marcarAulaComoUltima = (
  cursoId: string | number,
  aulaId: string | number,
) => {
  return api.update(`/admin/curso/${cursoId}/aula/${aulaId}/ultima-aula`, {});
};

// ============ ADMIN - VÍDEOS ============

/**
 * Lista todos os vídeos de uma aula
 */
const listarVideosAula = (
  cursoId: string | number,
  aulaId: string | number,
) => {
  return api.get(`/admin/curso/${cursoId}/aula/${aulaId}/video`);
};

/**
 * Busca um vídeo por ID
 */
const buscarVideoPorId = (
  cursoId: string | number,
  videoId: string | number,
) => {
  return api.get(`/admin/curso/${cursoId}/video/${videoId}`);
};

/**
 * Adiciona um vídeo do YouTube a uma aula
 */
const criarVideo = (
  cursoId: string | number,
  params: {
    aula_id: number;
    titulo: string;
    link_youtube: string;
    duracao_segundos?: number;
    ordem: number;
  },
) => {
  return api.create(`/admin/curso/${cursoId}/video`, params);
};

/**
 * Atualiza um vídeo existente
 */
const atualizarVideo = (
  cursoId: string | number,
  videoId: string | number,
  params: {
    titulo?: string;
    link_youtube?: string;
    duracao_segundos?: number;
    ordem?: number;
    aula_id?: number;
  },
) => {
  return api.update(`/admin/curso/${cursoId}/video/${videoId}`, params);
};

/**
 * Exclui um vídeo
 */
const deletarVideo = (cursoId: string | number, videoId: string | number) => {
  return api.delete(`/admin/curso/${cursoId}/video/${videoId}`);
};

// ============ ADMIN - MATERIAIS ============

/**
 * Faz upload de um material PDF para uma aula
 */
const uploadMaterial = (cursoId: string | number, formData: FormData) => {
  return api.create(`/admin/curso/${cursoId}/material`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * Exclui um material PDF
 */
const deletarMaterial = (
  cursoId: string | number,
  materialId: string | number,
) => {
  return api.delete(`/admin/curso/${cursoId}/material/${materialId}`);
};

// ============ ADMIN - SELOS ============

/**
 * Busca o selo de um curso
 */
const buscarSeloCurso = (cursoId: string | number) => {
  return api.get(`/admin/curso/${cursoId}/selo`);
};

/**
 * Cria um selo para um curso
 */
const criarSelo = (
  cursoId: string | number,
  params: {
    nome: string;
    descricao: string;
    imagem_url: string;
    imagem_key: string;
  },
) => {
  return api.create(`/admin/curso/${cursoId}/selo`, params);
};

/**
 * Atualiza um selo existente
 */
const atualizarSelo = (
  cursoId: string | number,
  seloId: string | number,
  params: {
    nome?: string;
    descricao?: string;
    imagem_url?: string;
    imagem_key?: string;
  },
) => {
  return api.update(`/admin/curso/${cursoId}/selo/${seloId}`, params);
};

/**
 * Exclui um selo
 */
const deletarSelo = (cursoId: string | number, seloId: string | number) => {
  return api.delete(`/admin/curso/${cursoId}/selo/${seloId}`);
};

// ============ ADMIN - QUESTIONÁRIOS ============

/**
 * Cria um questionário para uma aula
 */
const criarQuestionario = (
  cursoId: string | number,
  params: {
    aula_id?: number;
    final: boolean;
    titulo: string;
    descricao?: string;
    obrigatorio?: boolean;
    porcentagem_minima_aprovacao?: number;
  },
) => {
  return api.create(`/admin/curso/${cursoId}/questionario`, params);
};

/**
 * Busca um questionário completo com questões e alternativas
 */
const buscarQuestionarioAdmin = (
  cursoId: string | number,
  questionarioId: string | number,
) => {
  return api.get(`/admin/curso/${cursoId}/questionario/${questionarioId}`);
};

/**
 * Atualiza um questionário
 */
const atualizarQuestionario = (
  cursoId: string | number,
  questionarioId: string | number,
  params: {
    titulo?: string;
    descricao?: string;
    obrigatorio?: boolean;
    porcentagem_minima_aprovacao?: number;
  },
) => {
  return api.update(
    `/admin/curso/${cursoId}/questionario/${questionarioId}`,
    params,
  );
};

/**
 * Exclui um questionário
 */
const deletarQuestionario = (
  cursoId: string | number,
  questionarioId: string | number,
) => {
  return api.delete(`/admin/curso/${cursoId}/questionario/${questionarioId}`);
};

/**
 * Estatísticas de desempenho nos questionários do curso (admin).
 * GET /admin/curso/:cursoId/estatisticas-questionarios
 */
const getEstatisticasQuestionarios = (cursoId: string | number) => {
  return api.get(`/admin/curso/${cursoId}/estatisticas-questionarios`);
};

/**
 * Cria uma questão em um questionário
 */
const criarQuestao = (
  cursoId: string | number,
  questionarioId: string | number,
  params: {
    enunciado: string;
    tipo: string;
    ordem: number;
    ordenacaoAleatoria?: boolean;
  },
) => {
  return api.create(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao`,
    params,
  );
};

/**
 * Atualiza uma questão
 */
const atualizarQuestao = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
  params: {
    enunciado?: string;
    ordem?: number;
    ordenacaoAleatoria?: boolean;
  },
) => {
  return api.update(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}`,
    params,
  );
};

/**
 * Exclui uma questão
 */
const deletarQuestao = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
) => {
  return api.delete(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}`,
  );
};

/**
 * Cria uma alternativa para uma questão
 */
const criarAlternativa = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
  params: {
    texto: string;
    correta: boolean;
    ordem: number;
  },
) => {
  return api.create(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}/alternativa`,
    params,
  );
};

/**
 * Atualiza uma alternativa
 */
const atualizarAlternativa = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
  alternativaId: string | number,
  params: {
    texto?: string;
    correta?: boolean;
    ordem?: number;
  },
) => {
  return api.update(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}/alternativa/${alternativaId}`,
    params,
  );
};

/**
 * Marca uma alternativa como correta (desmarca automaticamente as outras)
 */
const marcarAlternativaCorreta = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
  alternativaId: string | number,
) => {
  return api.update(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}/alternativa/${alternativaId}/correta`,
    {},
  );
};

/**
 * Exclui uma alternativa
 */
const deletarAlternativa = (
  cursoId: string | number,
  questionarioId: string | number,
  questaoId: string | number,
  alternativaId: string | number,
) => {
  return api.delete(
    `/admin/curso/${cursoId}/questionario/${questionarioId}/questao/${questaoId}/alternativa/${alternativaId}`,
  );
};

// ============ ADMIN - CONFIGURACOES DO CERTIFICADO ============
/**
 * Retorna as configuracoes atuais
 */
const getConfiguracoesCursos = (unidadeId: number) => {
  return api.get(
    `/admin/configuracoes-curso?unidade_id=${unidadeId}`,
  ) as unknown as AxiosResponse<IConfiguracoesCurso>;
};
const setConfiguracoesCursos = (formDataToSend: FormData) => {
  return api.create(`/admin/configuracoes-curso`, formDataToSend, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// ============ ADMIN - VINCULO DE CARGOS ============
/**
 * Lista todos os cargos do curso
 */
const listarVinculosCargo = (
  cursoId: string | number,
  queryString?: string,
) => {
  return api.get(
    `/admin/curso/${cursoId}/cargo${queryString ? queryString : ""}`,
  );
};

/**
 * Cria um novo vinculo com cargo em um curso
 */
const criarVinculoCargo = (
  cursoId: string | number,
  cargoId: string | number,
) => {
  return api.create(`/admin/curso/${cursoId}/cargo`, {
    cargoId: cargoId,
  });
};

/**
 * Exclui uma cargo
 */
const deletarVinculoCargo = (
  cursoId: string | number,
  cargoId: string | number,
) => {
  return api.delete(`/admin/curso/${cursoId}/cargo/${cargoId}`);
};

export {
  // Cursos (Aluno)
  listarCursos,
  buscarCursoPorId,
  // Admin - Cursos
  listarCursosAdmin,
  buscarCursoPorIdAdmin,
  criarCurso,
  atualizarCurso,
  deletarCurso,
  // Progresso
  listarProgressos,
  buscarProgressoCurso,
  marcarVideoAssistido,
  gerarCertificadoCurso,
  // Questionários
  buscarQuestionarioPorId,
  responderQuestionario,
  verificarRespostaQuestionario,
  // Admin - Aulas
  criarAula,
  atualizarAula,
  deletarAula,
  marcarAulaComoUltima,
  // Admin - Selos
  buscarSeloCurso,
  criarSelo,
  atualizarSelo,
  deletarSelo,
  // Admin - Vídeos
  listarVideosAula,
  buscarVideoPorId,
  criarVideo,
  atualizarVideo,
  deletarVideo,
  // Admin - Materiais
  uploadMaterial,
  deletarMaterial,
  // Admin - Questionários
  criarQuestionario,
  buscarQuestionarioAdmin,
  atualizarQuestionario,
  deletarQuestionario,
  getEstatisticasQuestionarios,
  criarQuestao,
  atualizarQuestao,
  deletarQuestao,
  criarAlternativa,
  atualizarAlternativa,
  deletarAlternativa,
  marcarAlternativaCorreta,
  // Admin - Configuracoes certificado
  getConfiguracoesCursos,
  setConfiguracoesCursos,
  // Cargo
  listarVinculosCargo,
  criarVinculoCargo,
  deletarVinculoCargo,
};
